import { useMemo, useState } from 'react';
import { useCan } from '@/shared/hooks/useCan';
import {
  useDeleteGalleryMutation,
  useGalleryCountsQuery,
  useGalleryListQuery,
} from '@/features/gallery/api/useGalleryQueries';
import type { GalleryItem, GalleryListFilter, GalleryStatus } from '@/features/gallery/types/gallery';

/** Owns gallery list filters/pagination, upload/detail/delete modal state, and the list/counts queries. */
export function useGalleryPage() {
  const canUpload = useCan('upload-gallery-image');
  const canDelete = useCan('delete-gallery-image');

  const [filter, setFilter] = useState<GalleryListFilter>({
    page: 1,
    limit: 10,
    status: 'PENDING',
  });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<GalleryItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<GalleryItem | null>(null);

  const listQuery = useGalleryListQuery(filter);
  const countsQuery = useGalleryCountsQuery();
  const deleteMutation = useDeleteGalleryMutation();

  const items = listQuery.data?.data ?? [];
  const totalPages = listQuery.data?.totalPages || 1;
  const totalItems = listQuery.data?.total || 0;
  const counts = countsQuery.data?.data || {};

  const rangeLabel = useMemo(() => {
    if (!totalItems) return '0 contents';
    const start = (filter.page - 1) * filter.limit + 1;
    const end = Math.min(filter.page * filter.limit, totalItems);
    return `${start}–${end} of ${totalItems} contents`;
  }, [filter.page, filter.limit, totalItems]);

  const setStatus = (status: GalleryStatus) => {
    setFilter((prev) => ({ ...prev, status, page: 1 }));
  };

  const setPage = (updater: (page: number) => number) => {
    setFilter((prev) => ({ ...prev, page: updater(prev.page) }));
  };

  return {
    canUpload,
    canDelete,
    filter,
    setStatus,
    setPage,
    uploadOpen,
    setUploadOpen,
    detailItem,
    setDetailItem,
    deleteItem,
    setDeleteItem,
    items,
    totalPages,
    totalItems,
    counts,
    rangeLabel,
    isListPending: listQuery.isPending,
    deleteMutation,
  };
}
